import React from 'react';
import { Card, CardContent, Typography, Box, Grid, useTheme } from '@mui/material';
import { 
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Code as CodeIcon,
  People as PeopleIcon,
  LanOutlined as LanIcon,
  PieChartOutlined as PieChartIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

const MetricDisplay = ({ title, value, icon, color, subvalue, theme }) => (
  <Grid item xs={4}>
    <Box sx={{
      py: 0.75,
      px: 1.25,
      borderRadius: 1.5,
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Box
          sx={{
            backgroundColor: `${color}15`,
            color: color,
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            mr: 0.75,
            '& svg': { fontSize: '0.9rem' }
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" fontSize="0.7rem" fontWeight={500} noWrap>
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.2 }}>
          {value}
        </Typography>

        {subvalue && (
          <Typography variant="caption" color="text.secondary" fontSize="0.65rem" sx={{ ml: 0.5 }} noWrap>
            {subvalue}
          </Typography>
        )}
      </Box>
    </Box>
  </Grid>
);

export default function SystemActivity({ servers = [], agents = [] }) {
  const theme = useTheme();
  // Check if user has access to infrastructure data
  const hasInfrastructureAccess = servers.length > 0;
  
  // Calculate real metrics from server and agent data (using transformed status values)
  const totalNodes = servers.length;
  const activeNodes = servers.filter(server => server.status === 'Active').length;
  const totalAgents = agents.length;
  const activeAgents = agents.filter(agent => agent.isActive).length;
  
  // Calculate total resources from servers using real API data
  const totalCPUs = servers.reduce((sum, server) => {
    return sum + (server.nodeMetadata?.cpu_info?.cores || server.numOfCpus || 0);
  }, 0);
  const totalGPUs = servers.reduce((sum, server) => sum + (server.numGpus || server.numOfGpus || 0), 0);
  const totalMemoryGB = servers.reduce((sum, server) => {
    const memoryStr = server.nodeMetadata?.memory_info?.total || '0 GB';
    const memoryValue = parseFloat(memoryStr.replace(' GB', '')) || 0;
    return sum + memoryValue;
  }, 0);
  
  // Calculate unique node locations/regions
  const uniqueRegions = [...new Set(servers.map(server => 
    server.addressDetails?.region || 'unknown'
  ).filter(region => region !== 'unknown'))].length;
  
  const systemMetrics = {
    agents: {
      total: totalAgents,
      active: activeAgents,
      color: theme.palette.success.main,
      icon: <CodeIcon fontSize="small" />,
      subvalue: `${activeAgents} active`
    },
    ...(hasInfrastructureAccess && {
      nodes: {
        total: totalNodes,
        active: activeNodes,
        color: theme.palette.primary.main,
        icon: <StorageIcon fontSize="small" />,
        subvalue: `${activeNodes} active`
      },
      compute: {
        total: totalCPUs,
        color: theme.palette.secondary.main,
        icon: <MemoryIcon fontSize="small" />,
        subvalue: 'CPU cores'
      },
      gpus: {
        total: totalGPUs,
        color: theme.palette.warning.main,
        icon: <SpeedIcon fontSize="small" />,
        subvalue: 'GPUs total'
      },
      memory: {
        total: Math.round(totalMemoryGB),
        color: theme.palette.info.main,
        icon: <PeopleIcon fontSize="small" />,
        subvalue: 'GB RAM'
      },
      regions: {
        total: uniqueRegions || 1,
        color: theme.palette.success.main,
        icon: <LanIcon fontSize="small" />,
        subvalue: uniqueRegions > 1 ? 'regions' : 'region'
      }
    })
  };

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      color: 'text.primary',
      borderRadius: 3,
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`
    }}>
      <CardContent sx={{ 
        p: 0,
        '&:last-child': { pb: 0 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
            {hasInfrastructureAccess ? 'Infrastructure Overview' : 'System Overview'}
          </Typography>
        </Box>
        
        <Box sx={{ p: 1, flex: 1, display: 'flex', alignItems: 'center' }}>
          <Grid container spacing={1}>
            <MetricDisplay
              title="Agents"
              value={systemMetrics.agents.total}
              icon={systemMetrics.agents.icon}
              color={systemMetrics.agents.color}
              subvalue={systemMetrics.agents.subvalue}
              theme={theme}
            />
            
            {hasInfrastructureAccess && (
              <>
                <MetricDisplay
                  title="Nodes"
                  value={systemMetrics.nodes.total}
                  icon={systemMetrics.nodes.icon}
                  color={systemMetrics.nodes.color}
                  subvalue={systemMetrics.nodes.subvalue}
                  theme={theme}
                />

                <MetricDisplay
                  title="CPU Cores"
                  value={systemMetrics.compute.total}
                  icon={systemMetrics.compute.icon}
                  color={systemMetrics.compute.color}
                  subvalue={systemMetrics.compute.subvalue}
                  theme={theme}
                />

                <MetricDisplay
                  title="GPUs"
                  value={systemMetrics.gpus.total}
                  icon={systemMetrics.gpus.icon}
                  color={systemMetrics.gpus.color}
                  subvalue={systemMetrics.gpus.subvalue}
                  theme={theme}
                />

                <MetricDisplay
                  title="Memory"
                  value={systemMetrics.memory.total}
                  icon={systemMetrics.memory.icon}
                  color={systemMetrics.memory.color}
                  subvalue={systemMetrics.memory.subvalue}
                  theme={theme}
                />

                <MetricDisplay
                  title="Regions"
                  value={systemMetrics.regions.total}
                  icon={systemMetrics.regions.icon}
                  color={systemMetrics.regions.color}
                  subvalue={systemMetrics.regions.subvalue}
                  theme={theme}
                />
              </>
            )}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
} 